import React, { useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, useCreateForm, useUpdateForm, useFormUsage } from '../hooks';
import {
  useChecklistTemplate,
  useCreateChecklistTemplate,
  useUpdateChecklistTemplate,
} from '@/modules/checklists/hooks/useChecklistTemplates';
import type {
  FormField, FormFieldType, CreateFormRequest, UpdateFormRequest,
  FormType, PhotoFieldConfig, ChecklistSettings, FormContactDataSettings, FormSettings,
} from '@troia-v3/types';
import { DEFAULT_FORM_CONTACT_DATA } from '@troia-v3/types';
import { FormContactDataSection } from '../components/FormContactDataSection';
import { FormResponseSettingsSection } from '../components/FormResponseSettingsSection';
import { FormFieldPreview } from '../components/FormFieldPreview';
import { FormPreviewModal } from '../components/FormPreviewModal';
import { toast } from 'sonner';
import { Button, Select, Input, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { DUE_UNIT_OPTIONS, splitDueMinutes, toDueMinutes, type DueUnit } from '@/shared/utils/dueMinutes';

const FIELD_TYPE_OPTIONS: { type: FormFieldType; label: string; icon: string }[] = [
  { type: 'short_text' as FormFieldType, label: 'Texto curto', icon: 'Aa' },
  { type: 'long_text' as FormFieldType, label: 'Texto longo', icon: 'Tt' },
  { type: 'email' as FormFieldType, label: 'Email', icon: '@' },
  { type: 'phone' as FormFieldType, label: 'Telefone', icon: '#' },
  { type: 'number' as FormFieldType, label: 'Número', icon: '1' },
  { type: 'select' as FormFieldType, label: 'Dropdown', icon: 'v' },
  { type: 'multi_select' as FormFieldType, label: 'Multi-seleção', icon: '[]' },
  { type: 'radio' as FormFieldType, label: 'Múltipla escolha', icon: 'O' },
  { type: 'checkbox' as FormFieldType, label: 'Caixas de seleção', icon: '✓' },
  { type: 'date' as FormFieldType, label: 'Data', icon: 'D' },
  { type: 'time' as FormFieldType, label: 'Hora', icon: 'T' },
  { type: 'rating' as FormFieldType, label: 'Avaliação', icon: '★' },
  { type: 'linear_scale' as FormFieldType, label: 'Escala linear', icon: '—' },
  { type: 'file_upload' as FormFieldType, label: 'Upload de arquivo', icon: '↑' },
  { type: 'url' as FormFieldType, label: 'URL', icon: '🔗' },
  { type: 'cpf' as FormFieldType, label: 'CPF', icon: 'ID' },
  { type: 'cnpj' as FormFieldType, label: 'CNPJ', icon: 'CN' },
  { type: 'heading' as FormFieldType, label: 'Cabeçalho', icon: 'H' },
  { type: 'paragraph' as FormFieldType, label: 'Parágrafo', icon: '¶' },
  // Exclusivos do modo checklist (type: 'checklist') — escondidos no modo form (spec §4.2).
  { type: 'photo' as FormFieldType, label: 'Foto/Evidência', icon: 'FT' },
  { type: 'conformity' as FormFieldType, label: 'Conformidade', icon: 'CF' },
];

/** Campos que só existem no modo checklist — filtrados da paleta/seletor de tipo no modo form. */
const CHECKLIST_ONLY_FIELD_TYPES: FormFieldType[] = ['photo' as FormFieldType, 'conformity' as FormFieldType];

// DS palette grouping — same 19 field types, organized by category with accent color per group.
// Purely presentational: each item still calls addField(type). Accent colors are theme-independent
// decorative badges (white glyph on saturated square) and read correctly in light and dark.
const FIELD_CATEGORIES: { title: string; items: { type: FormFieldType; color: string }[] }[] = [
  {
    title: 'Campos básicos',
    items: [
      { type: 'short_text' as FormFieldType, color: '#8b5cf6' },
      { type: 'long_text' as FormFieldType, color: '#8b5cf6' },
      { type: 'email' as FormFieldType, color: '#0d9488' },
      { type: 'phone' as FormFieldType, color: '#0d9488' },
      { type: 'number' as FormFieldType, color: '#0d9488' },
      { type: 'url' as FormFieldType, color: '#0d9488' },
    ],
  },
  {
    title: 'Seleção',
    items: [
      { type: 'select' as FormFieldType, color: '#3b82f6' },
      { type: 'multi_select' as FormFieldType, color: '#3b82f6' },
      { type: 'radio' as FormFieldType, color: '#3b82f6' },
      { type: 'checkbox' as FormFieldType, color: '#3b82f6' },
    ],
  },
  {
    title: 'Data e avaliação',
    items: [
      { type: 'date' as FormFieldType, color: '#f59e0b' },
      { type: 'time' as FormFieldType, color: '#f59e0b' },
      { type: 'rating' as FormFieldType, color: '#f59e0b' },
      { type: 'linear_scale' as FormFieldType, color: '#f59e0b' },
    ],
  },
  {
    title: 'Documentos',
    items: [
      { type: 'cpf' as FormFieldType, color: '#0369a1' },
      { type: 'cnpj' as FormFieldType, color: '#0369a1' },
    ],
  },
  {
    title: 'Arquivo e layout',
    items: [
      { type: 'file_upload' as FormFieldType, color: '#be123c' },
      { type: 'heading' as FormFieldType, color: '#64748b' },
      { type: 'paragraph' as FormFieldType, color: '#64748b' },
    ],
  },
];

// Categoria exclusiva do modo checklist — apenas anexada à paleta quando `isChecklist` (spec §4.2).
const CHECKLIST_FIELD_CATEGORY: { title: string; items: { type: FormFieldType; color: string }[] } = {
  title: 'Checklist',
  items: [
    { type: 'photo' as FormFieldType, color: '#dc2626' },
    { type: 'conformity' as FormFieldType, color: '#16a34a' },
  ],
};

const DEFAULT_CHECKLIST_SETTINGS: ChecklistSettings = {
  requiresApproval: false,
  scoreEnabled: false,
  captureGps: false,
};

const generateFieldId = (): string => {
  return `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

export interface FormBuilderPageProps {
  /**
   * Trava o modo do builder (rota `/checklists/templates/new` — Task 14).
   * Quando ausente, cai no query param `?type=` e por fim no default `'form'`.
   */
  type?: FormType;
}

export const FormBuilderPage: React.FC<FormBuilderPageProps> = ({ type: lockedType }) => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  // O builder atende DOIS backends: formulários (`/api/forms`, permissão
  // `forms:*`) e modelos de checklist (`/api/checklists/templates`, permissão
  // `checklists:*`). Quem decide é a ROTA (`lockedType`) — não o `formType`,
  // que na edição só se resolve DEPOIS de carregar o documento, tarde demais
  // para escolher de onde carregá-lo. Um nível com Checklists e sem
  // Formulários precisa que o modo checklist NUNCA toque em `/api/forms`.
  const isChecklistRoute = lockedType === 'checklist';

  const formQuery = useForm(isChecklistRoute ? undefined : id);
  const templateQuery = useChecklistTemplate(isChecklistRoute ? id : undefined);
  const existingForm = isChecklistRoute ? templateQuery.data : formQuery.data;
  const isLoading = isChecklistRoute ? templateQuery.isLoading : formQuery.isLoading;

  // Spec §6: editar um modelo em uso não altera aplicações (snapshot congelado),
  // mas apagar uma pergunta faz o filtro dela sumir do CRM — avisar antes.
  const { data: usage } = useFormUsage(id, isEditing);
  const applications = usage?.applications ?? 0;

  const createForm = useCreateForm();
  const updateForm = useUpdateForm();
  const createTemplate = useCreateChecklistTemplate();
  const updateTemplate = useUpdateChecklistTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<FormField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // `type` do form/checklist (Task 14): prop (rota travada) > query param > 'form'.
  // No modo de edição, é sobrescrito pelo `existingForm.type` real assim que carrega.
  const [formType, setFormType] = useState<FormType>(
    lockedType ?? (searchParams.get('type') === 'checklist' ? 'checklist' : 'form')
  );
  const [checklistSettings, setChecklistSettings] = useState<ChecklistSettings>(DEFAULT_CHECKLIST_SETTINGS);
  const isChecklist = formType === 'checklist';
  // Configurações do formulário (painel sem campo selecionado). Formulário
  // novo nasce pedindo os dados de contato (2026-09-29); o resto nasce
  // desligado. Formulário antigo sem `contactData` é anônimo.
  const [formSettings, setFormSettings] = useState<FormSettings>({
    allowMultipleResponses: true,
    showProgressBar: false,
    contactData: DEFAULT_FORM_CONTACT_DATA,
  });
  // Sem os dados de contato não há como reconhecer a pessoa: volta a aceitar repetição.
  const setContactData = (contactData: FormContactDataSettings): void =>
    setFormSettings((prev) => ({
      ...prev,
      contactData,
      ...(contactData.enabled ? {} : { allowMultipleResponses: true }),
    }));

  /**
   * Unidade "lembrada" só para o caso de CAMPO VAZIO — com valor definido, a
   * unidade continua 100% derivada de `checklistSettings.defaultDueMinutes`
   * via `splitDueMinutes` (linha abaixo), então a chegada tardia do
   * `existingForm` (fetch assíncrono, efeito `initialized` mais abaixo) já
   * resolve sozinha: no primeiro render com o registro carregado, cai direto
   * no ramo derivado (ex.: 1440 → "1 dia") sem depender deste estado local.
   * `emptyDefaultDueUnit` só importa enquanto NÃO há valor gravado.
   */
  const [emptyDefaultDueUnit, setEmptyDefaultDueUnit] = useState<DueUnit>('hours');

  // O builder NÃO remonta ao navegar de um formulário/modelo para outro
  // (mesma rota, só o `:id` muda) — sem este reset, a unidade "lembrada" no
  // vazio pro formulário A vazaria pro formulário B. Espelha o reset por
  // `nodeId` do CreateChecklistConfig.tsx (painel de node do workflow).
  React.useEffect(() => {
    setEmptyDefaultDueUnit('hours');
  }, [id]);

  const prazoPadrao = checklistSettings.defaultDueMinutes !== undefined
    ? splitDueMinutes(checklistSettings.defaultDueMinutes)
    : { value: undefined as number | undefined, unit: emptyDefaultDueUnit };

  const aplicarPrazoPadrao = (valorCru: string, unidade: DueUnit): void => {
    if (valorCru.trim() === '') {
      setEmptyDefaultDueUnit(unidade);
      setChecklistSettings((prev) => ({ ...prev, defaultDueMinutes: undefined }));
      return;
    }
    const n = parseInt(valorCru, 10);
    if (Number.isNaN(n)) {
      setEmptyDefaultDueUnit(unidade);
      setChecklistSettings((prev) => ({ ...prev, defaultDueMinutes: undefined }));
      return;
    }
    const minutos = Math.min(Math.max(toDueMinutes(n, unidade), 1), 43_200);
    setChecklistSettings((prev) => ({ ...prev, defaultDueMinutes: minutos }));
  };

  // Preview INTERNO — só no modo checklist. O preview público (`/f/:slug`) não
  // serve para checklists: o backend fecha o formulário público para
  // `type: 'checklist'` (404 por design) porque um checklist é preenchido por um
  // responsável autenticado em `/checklists/:id/fill`, nunca por link público.
  const [previewOpen, setPreviewOpen] = useState(false);

  // Initialize form data when editing
  React.useEffect(() => {
    if (existingForm && !initialized) {
      setName(existingForm.name);
      setDescription(existingForm.description || '');
      setFields(existingForm.fields || []);
      setFormType(existingForm.type ?? 'form');
      setChecklistSettings(existingForm.checklistSettings ?? DEFAULT_CHECKLIST_SETTINGS);
      setFormSettings({
        ...existingForm.settings,
        contactData: existingForm.settings?.contactData ?? { ...DEFAULT_FORM_CONTACT_DATA, enabled: false },
      });
      setInitialized(true);
    }
  }, [existingForm, initialized]);

  const selectedField = fields.find(f => f.id === selectedFieldId);

  const addField = useCallback((type: FormFieldType) => {
    const newField: FormField = {
      id: generateFieldId(),
      type,
      label: FIELD_TYPE_OPTIONS.find(o => o.type === type)?.label || 'Novo campo',
      required: false,
      order: fields.length,
      options: ['select', 'multi_select', 'radio', 'checkbox'].includes(type) ? [
        { id: generateFieldId(), label: 'Opção 1', value: 'opcao_1' },
      ] : undefined,
      photoConfig: type === 'photo' ? { source: 'any' } : undefined,
    };
    setFields(prev => [...prev, newField]);
    setSelectedFieldId(newField.id);
  }, [fields.length]);

  const updateField = useCallback((fieldId: string, updates: Partial<FormField>) => {
    setFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  }, []);

  const removeField = useCallback((fieldId: string) => {
    setFields(prev => prev.filter(f => f.id !== fieldId));
    if (selectedFieldId === fieldId) setSelectedFieldId(null);
  }, [selectedFieldId]);

  const moveField = useCallback((fieldId: string, direction: 'up' | 'down') => {
    setFields(prev => {
      const idx = prev.findIndex(f => f.id === fieldId);
      if (idx === -1) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const newFields = [...prev];
      [newFields[idx], newFields[newIdx]] = [newFields[newIdx], newFields[idx]];
      return newFields.map((f, i) => ({ ...f, order: i }));
    });
  }, []);

  const isSaving = createForm.isPending || updateForm.isPending
    || createTemplate.isPending || updateTemplate.isPending;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Nome do formulário é obrigatório');
      return;
    }
    if (fields.length === 0) {
      toast.error('Adicione pelo menos um campo');
      return;
    }

    const orderedFields = fields.map((f, i) => ({ ...f, order: i }));

    // `type` SEMPRE vai no payload (nos dois modos) — ruling da Task 6: o
    // `updateFormSchema` do backend exige `type` quando há checklistSettings/
    // campos checklist, e PUT é full-replace (convenção da casa).
    // `settings` vai inteiro (o backend grava o objeto todo). Mesmas regras
    // do backend, para avisar antes de salvar.
    const settings: FormSettings = formSettings;
    if (!isChecklist) {
      const email = settings.confirmationEmail;
      if (email?.enabled && (!email.channelId || !email.subject?.trim() || !email.body?.trim())) {
        toast.error('E-mail de confirmação: escolha o canal e escreva o assunto e a mensagem');
        return;
      }
      if (settings.notifications?.enabled && settings.notifications.userIds.length === 0) {
        toast.error('Avisar a equipe: escolha quem será avisado');
        return;
      }
    }
    const basePayload = {
      name,
      description: description || undefined,
      type: formType,
      fields: orderedFields,
      ...(isChecklist ? { checklistSettings } : { settings }),
    };
    const listPath = isChecklist ? '/checklists/templates' : '/automation/forms';

    // Mesmo payload, backend diferente conforme a rota (ver `isChecklistRoute`).
    if (isEditing && id) {
      const data: UpdateFormRequest = basePayload;
      if (isChecklistRoute) await updateTemplate.mutateAsync({ id, data });
      else await updateForm.mutateAsync({ id, data });
      navigate(listPath);
    } else {
      const data: CreateFormRequest = basePayload;
      if (isChecklistRoute) await createTemplate.mutateAsync(data);
      else await createForm.mutateAsync(data);
      navigate(listPath);
    }
  };

  if (isEditing && isLoading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <p style={{ color: 'var(--theme-muted-text)' }}>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header — DS toolbar: breadcrumb (never back arrow) + primary action */}
      <div
        className="flex-shrink-0 flex items-center gap-3 px-4"
        style={{
          height: 52,
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <nav className="flex items-center gap-1.5 min-w-0 flex-1">
          <span className="text-sm font-medium flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>
            Automação
          </span>
          <Icon name="chevron-right" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <button
            onClick={() => navigate('/automation/forms')}
            data-testid="form-builder-breadcrumb-forms"
            className="text-sm font-medium flex-shrink-0 transition-colors"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
          >
            Formulários
          </button>
          <Icon name="chevron-right" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do formulário"
            data-testid="form-builder-name-input"
            className="text-[15px] font-semibold bg-transparent border-none outline-none min-w-0 flex-1"
            style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.01em' }}
          />
        </nav>

        {/* Ações do toolbar — Button shared (F01-03) */}
        <Button
          variant="secondary"
          icon={isChecklist ? 'eye' : 'external-link'}
          className="flex-shrink-0"
          onClick={() => {
            if (isChecklist) {
              // Modal interno: não depende de slug salvo e não cria instância.
              setPreviewOpen(true);
            } else if (existingForm?.slug) {
              window.open(`/f/${existingForm.slug}`, '_blank', 'noopener');
            } else {
              toast.error('Salve o formulário antes de pré-visualizar');
            }
          }}
          data-testid="form-builder-preview"
        >
          Pré-visualizar
        </Button>

        <Button
          variant="primary"
          icon="save"
          className="flex-shrink-0"
          onClick={handleSave}
          disabled={isSaving}
          loading={isSaving}
          data-testid="form-builder-save"
        >
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>

      {isEditing && applications > 0 && (
        <div
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-[13px]"
          style={{ backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a', color: '#92400e' }}
          data-testid="form-builder-usage-warning"
        >
          <Icon name="alert-triangle" className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>{applications} {applications === 1 ? 'aplicação usa' : 'aplicações usam'} este modelo.</strong>{' '}
            Alterar campos não muda as aplicações já preenchidas; remover uma pergunta faz o filtro dela sumir do CRM.
          </span>
        </div>
      )}

      {/* Main Content: 3-column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Field Type Palette (desktop; no mobile o canvas vira preview read-only — #268) */}
        <div
          className="hidden md:block w-60 flex-shrink-0 border-r overflow-y-auto"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
          data-testid="form-builder-palette"
        >
          <div className="p-3">
            {(isChecklist ? [...FIELD_CATEGORIES, CHECKLIST_FIELD_CATEGORY] : FIELD_CATEGORIES).map((category) => (
              <div key={category.title} className="mb-1">
                <p
                  className="text-[11px] font-bold uppercase px-1 pt-3 pb-2"
                  style={{ color: 'var(--theme-muted-text)', letterSpacing: '0.04em' }}
                >
                  {category.title}
                </p>
                <div className="space-y-1.5">
                  {category.items.map((item) => {
                    const meta = FIELD_TYPE_OPTIONS.find((o) => o.type === item.type);
                    return (
                      <button
                        key={item.type}
                        onClick={() => addField(item.type)}
                        data-testid={`form-palette-add-${item.type}`}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium text-left border transition-colors"
                        style={{
                          color: 'var(--theme-card-text)',
                          borderColor: 'var(--theme-card-border)',
                          backgroundColor: 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                          e.currentTarget.style.borderColor = 'var(--theme-input-border)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = 'var(--theme-card-border)';
                        }}
                      >
                        <span
                          className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold font-mono text-white flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                        >
                          {meta?.icon}
                        </span>
                        <span className="min-w-0 truncate">{meta?.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Center: Form Canvas — cartão de formulário único WYSIWYG (mockup .fb-form) */}
        <div className="flex-1 overflow-y-auto" style={{ backgroundColor: 'var(--theme-page-background)' }}>
          <div className="max-w-xl mx-auto py-8 px-4 sm:px-6">
            <div
              className="rounded-2xl border"
              style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
            >
              <div className="p-6 sm:p-[26px] space-y-5">
                {/* Título + descrição do formulário (preview) */}
                <div>
                  <h2 className="text-xl font-bold tracking-tight" style={{ color: 'var(--theme-card-text)' }}>
                    {name || 'Formulário sem título'}
                  </h2>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descrição do formulário (opcional)"
                    className="w-full text-sm resize-none mt-1 bg-transparent border-none outline-none p-0"
                    rows={2}
                    style={{ color: 'var(--theme-muted-text)' }}
                    data-testid="form-builder-description"
                  />
                </div>

                {/* Empty state */}
                {fields.length === 0 && (
                  <div
                    className="border-2 border-dashed rounded-xl p-12 text-center"
                    style={{ borderColor: 'var(--theme-card-border)' }}
                  >
                    <Icon name="type" className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--theme-muted-text)' }} />
                    <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                      Clique em um tipo de campo na barra lateral para adicionar
                    </p>
                  </div>
                )}

                {/* Fields — borda só no hover/seleção (mockup .fb-field) */}
                <div className="space-y-1">
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      className={`group relative rounded-lg p-3 -mx-1 cursor-pointer transition-all border-2 ${
                        selectedFieldId === field.id
                          ? 'border-solid'
                          : 'border-dashed border-transparent hover:[border-color:var(--theme-card-border)]'
                      }`}
                      style={selectedFieldId === field.id ? { borderColor: 'var(--theme-primary)' } : undefined}
                      onClick={() => setSelectedFieldId(field.id)}
                      data-testid={`form-field-card-${field.id}`}
                    >
                      {/* Preview WYSIWYG do campo (#262) */}
                      <FormFieldPreview field={field} />

                      {/* Controles — canto sup. direito, aparecem no hover/selecionado (não no mobile: leitura) */}
                      <div
                        className={`hidden md:flex absolute top-2 right-2 items-center gap-0.5 rounded-lg px-1 py-0.5 transition-opacity ${
                          selectedFieldId === field.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}
                        style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
                      >
                        {index > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); moveField(field.id, 'up'); }}
                            className="p-1 rounded text-xs" style={{ color: 'var(--theme-muted-text)' }} title="Mover para cima">↑</button>
                        )}
                        {index < fields.length - 1 && (
                          <button onClick={(e) => { e.stopPropagation(); moveField(field.id, 'down'); }}
                            className="p-1 rounded text-xs" style={{ color: 'var(--theme-muted-text)' }} title="Mover para baixo">↓</button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                          className="p-1 rounded transition-colors"
                          style={{ color: 'var(--theme-error-color)' }}
                          title="Remover campo"
                          data-testid={`form-field-remove-${field.id}`}
                        >
                          <Icon name="trash" className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Botão Enviar simulado (mockup .fb-submit) */}
                {fields.length > 0 && (
                  <button
                    type="button"
                    disabled
                    className="w-full h-11 rounded-lg text-sm font-semibold mt-2"
                    style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff', cursor: 'default' }}
                  >
                    Enviar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Field Editor Panel — campo selecionado; sem campo, as configurações do checklist ou do formulário (desktop only — #268) */}
        {(

          <div
            className="hidden md:block w-80 flex-shrink-0 border-l overflow-y-auto"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
            data-testid="form-builder-side-panel"
          >
            <div className="p-4">
              {selectedField ? (
              <>
              <div className="flex items-center gap-2.5 mb-4">
                <span
                  className="flex items-center justify-center flex-none rounded-[9px]"
                  style={{ width: '30px', height: '30px', backgroundColor: 'var(--theme-primary)' }}
                >
                  <Icon name="edit" className="w-4 h-4" style={{ color: '#ffffff' }} />
                </span>
                <p className="text-sm font-bold" style={{ color: 'var(--theme-card-text)' }}>
                  {selectedField.label}
                </p>
              </div>

              <div className="space-y-4">
                {/* Tipo do campo (#263) — troca o tipo; photo/conformity só aparecem no modo checklist */}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                    Tipo
                  </label>
                  <select
                    value={selectedField.type}
                    onChange={(e) => updateField(selectedField.id, { type: e.target.value as FormFieldType })}
                    className="w-full text-sm border rounded-lg px-3 py-2 h-[38px]"
                    style={{ backgroundColor: 'var(--theme-input-background)', borderColor: 'var(--theme-input-border)', color: 'var(--theme-input-text)' }}
                    data-testid="form-field-type"
                  >
                    {(isChecklist ? FIELD_TYPE_OPTIONS : FIELD_TYPE_OPTIONS.filter((o) => !CHECKLIST_ONLY_FIELD_TYPES.includes(o.type))).map((o) => (
                      <option key={o.type} value={o.type}>{o.label}</option>
                    ))}
                  </select>
                </div>

                {/* Label */}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                    Rótulo
                  </label>
                  <input
                    type="text"
                    value={selectedField.label}
                    onChange={(e) => updateField(selectedField.id, { label: e.target.value })}
                    className="w-full text-sm border rounded-lg px-3 py-2 h-[38px]"
                    style={{
                      backgroundColor: 'var(--theme-input-background)',
                      borderColor: 'var(--theme-input-border)',
                      color: 'var(--theme-input-text)',
                    }}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                    Descrição
                  </label>
                  <input
                    type="text"
                    value={selectedField.description || ''}
                    onChange={(e) => updateField(selectedField.id, { description: e.target.value || undefined })}
                    placeholder="Texto de ajuda"
                    className="w-full text-sm border rounded-lg px-3 py-2 h-[38px]"
                    style={{
                      backgroundColor: 'var(--theme-input-background)',
                      borderColor: 'var(--theme-input-border)',
                      color: 'var(--theme-input-text)',
                    }}
                  />
                </div>

                {/* Placeholder */}
                {!['heading', 'paragraph', 'checkbox', 'radio', 'rating', 'linear_scale', 'file_upload'].includes(selectedField.type) && (
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                      Placeholder
                    </label>
                    <input
                      type="text"
                      value={selectedField.placeholder || ''}
                      onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value || undefined })}
                      className="w-full text-sm border rounded-lg px-3 py-2 h-[38px]"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        borderColor: 'var(--theme-input-border)',
                        color: 'var(--theme-input-text)',
                      }}
                    />
                  </div>
                )}

                {/* Configuração do campo Foto/Evidência (photoConfig) — só quando type === 'photo' */}
                {selectedField.type === 'photo' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                        Origem da foto
                      </label>
                      <Select
                        value={selectedField.photoConfig?.source ?? 'any'}
                        onChange={(v) => updateField(selectedField.id, {
                          photoConfig: {
                            ...selectedField.photoConfig,
                            source: v as PhotoFieldConfig['source'],
                          },
                        })}
                        options={[
                          { value: 'any', label: 'Galeria ou câmera' },
                          { value: 'camera_only', label: 'Somente câmera' },
                        ]}
                        data-testid="form-field-photo-source"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                        Máximo de fotos
                      </label>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={selectedField.photoConfig?.maxPhotos ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const parsed = raw === '' ? undefined : Math.min(10, Math.max(1, Number(raw)));
                          updateField(selectedField.id, {
                            photoConfig: {
                              source: selectedField.photoConfig?.source ?? 'any',
                              maxPhotos: parsed,
                            },
                          });
                        }}
                        data-testid="form-field-photo-maxphotos"
                      />
                    </div>
                  </>
                )}

                {/* Required toggle */}
                {!['heading', 'paragraph'].includes(selectedField.type) && (
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium" style={{ color: 'var(--theme-muted-text)' }}>
                      Obrigatório
                    </label>
                    <button
                      onClick={() => updateField(selectedField.id, { required: !selectedField.required })}
                      className="w-10 h-[23px] rounded-full transition-colors relative"
                      style={{
                        backgroundColor: selectedField.required ? 'var(--theme-primary)' : 'var(--theme-input-border)',
                      }}
                    >
                      <div
                        className="w-[19px] h-[19px] rounded-full bg-white absolute top-0.5 transition-all"
                        style={{ left: selectedField.required ? '19px' : '2px' }}
                      />
                    </button>
                  </div>
                )}

                {/* Options editor for choice fields */}
                {['select', 'multi_select', 'radio', 'checkbox'].includes(selectedField.type) && (
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                      Opções
                    </label>
                    <div className="space-y-2">
                      {(selectedField.options || []).map((option, idx) => (
                        <div key={option.id} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={option.label}
                            onChange={(e) => {
                              const newOptions = [...(selectedField.options || [])];
                              newOptions[idx] = {
                                ...option,
                                label: e.target.value,
                                value: e.target.value.toLowerCase().replace(/\s+/g, '_')
                              };
                              updateField(selectedField.id, { options: newOptions });
                            }}
                            className="flex-1 text-sm border rounded px-2 py-1 h-[38px]"
                            style={{
                              backgroundColor: 'var(--theme-input-background)',
                              borderColor: 'var(--theme-input-border)',
                              color: 'var(--theme-input-text)',
                            }}
                          />
                          <button
                            onClick={() => {
                              const newOptions = (selectedField.options || []).filter((_, i) => i !== idx);
                              updateField(selectedField.id, { options: newOptions });
                            }}
                            className="p-1 rounded"
                            style={{ color: 'var(--theme-error-color)' }}
                          >
                            <Icon name="trash" className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newOption = {
                            id: generateFieldId(),
                            label: `Opção ${(selectedField.options || []).length + 1}`,
                            value: `opcao_${(selectedField.options || []).length + 1}`,
                          };
                          updateField(selectedField.id, {
                            options: [...(selectedField.options || []), newOption]
                          });
                        }}
                        className="flex items-center gap-1 text-xs font-medium"
                        style={{ color: 'var(--theme-primary)' }}
                      >
                        <Icon name="plus" className="w-3 h-3" />
                        Adicionar opção
                      </button>
                    </div>
                  </div>
                )}

                {/* Remover campo (#263) */}
                <button
                  onClick={() => removeField(selectedField.id)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors mt-2"
                  style={{ color: 'var(--theme-error-color)', borderColor: 'var(--theme-error-color)' }}
                  data-testid="form-field-panel-remove"
                >
                  <Icon name="trash" className="w-4 h-4" />
                  Remover campo
                </button>
              </div>
              </>
              ) : !isChecklist ? (
              <>
              {/* Configurações do formulário (2026-09-29) — quando nenhum campo está selecionado */}
              <div className="flex items-center gap-2.5 mb-4">
                <span
                  className="flex items-center justify-center flex-none rounded-[9px]"
                  style={{ width: '30px', height: '30px', backgroundColor: 'var(--theme-primary)' }}
                >
                  <Icon name="settings" className="w-4 h-4" style={{ color: '#ffffff' }} />
                </span>
                <p className="text-sm font-bold" style={{ color: 'var(--theme-card-text)' }}>
                  Configurações do formulário
                </p>
              </div>
              <FormContactDataSection
                value={formSettings.contactData ?? { ...DEFAULT_FORM_CONTACT_DATA, enabled: false }}
                onChange={setContactData}
              />
              <div className="mt-6 pt-5" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
                <FormResponseSettingsSection value={formSettings} onChange={setFormSettings} />
              </div>
              </>
              ) : (
              <>
              {/* Configurações do checklist (checklistSettings) — visível só no modo checklist, quando nenhum campo está selecionado */}
              <div className="flex items-center gap-2.5 mb-4">
                <span
                  className="flex items-center justify-center flex-none rounded-[9px]"
                  style={{ width: '30px', height: '30px', backgroundColor: 'var(--theme-primary)' }}
                >
                  <Icon name="settings" className="w-4 h-4" style={{ color: '#ffffff' }} />
                </span>
                <p className="text-sm font-bold" style={{ color: 'var(--theme-card-text)' }}>
                  Configurações do checklist
                </p>
              </div>

              <div className="space-y-4" data-testid="checklist-settings-section">
                <div data-testid="checklist-settings-requires-approval">
                  <Switch
                    label="Exigir aprovação do gestor"
                    checked={checklistSettings.requiresApproval}
                    onChange={(checked) => setChecklistSettings((prev) => ({ ...prev, requiresApproval: checked }))}
                  />
                </div>
                <div data-testid="checklist-settings-score-enabled">
                  <Switch
                    label="Calcular pontuação de conformidade"
                    checked={checklistSettings.scoreEnabled}
                    onChange={(checked) => setChecklistSettings((prev) => ({ ...prev, scoreEnabled: checked }))}
                  />
                </div>
                <div data-testid="checklist-settings-capture-gps">
                  <Switch
                    label="Capturar localização no envio"
                    checked={checklistSettings.captureGps}
                    onChange={(checked) => setChecklistSettings((prev) => ({ ...prev, captureGps: checked }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                    Prazo padrão
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="number"
                      min={1}
                      value={prazoPadrao.value === undefined ? '' : String(prazoPadrao.value)}
                      onChange={(e) => aplicarPrazoPadrao(e.target.value, prazoPadrao.unit)}
                      data-testid="form-builder-default-due-value"
                    />
                    <Select
                      value={prazoPadrao.unit}
                      onChange={(unidade) => aplicarPrazoPadrao(
                        prazoPadrao.value === undefined ? '' : String(prazoPadrao.value),
                        unidade as DueUnit,
                      )}
                      options={DUE_UNIT_OPTIONS}
                      data-testid="form-builder-default-due-unit"
                    />
                  </div>
                  {/* Espelha o Zod (`int().min(1).max(43_200)`, 30 dias) — o clamp
                      acontece em aplicarPrazoPadrao, já convertido para minutos. */}
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Preview interno do modelo de checklist (substitui `/f/:slug`, fechado
          para checklists no backend). Lê o estado ATUAL do builder — pré-visualiza
          alterações ainda não salvas. */}
      <FormPreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        name={name}
        {...(description ? { description } : {})}
        fields={fields}
        data-testid="checklist-template-preview-modal"
      />
    </div>
  );
};
